import { oc } from "@orpc/contract";
import { z } from "zod";

const UUIDSchema = z.uuid();
const DecimalStringSchema = z.string().regex(/^-?\d+(\.\d+)?$/, {
  error: "Must be a numeric string",
});

export const ScheduledPaymentSchema = z.object({
  apInvoiceId: UUIDSchema,
  supplierId: UUIDSchema.nullable(),
  supplierName: z.string().nullable(),
  invoiceNumber: z.string().nullable(),
  dueDate: z.string().nullable(),
  scheduledPaymentDate: z.string().nullable(),
  paymentStatus: z.string(),
  paymentHold: z.boolean(),
  paymentHoldReason: z.string().nullable(),
  totalAmount: DecimalStringSchema,
  currency: z.string(),
  paymentMethod: z.string(),
  payTerms: z.string().nullable(),
  preferredPayTerms: z.string().nullable(),
});

export const ListScheduledPaymentsInputSchema = z.object({
  supplierId: UUIDSchema.optional(),
  paymentStatus: z.string().optional(),
  paymentHold: z.boolean().optional(),
  scheduledFrom: z.string().optional(),
  scheduledTo: z.string().optional(),
  limit: z.int().min(1).max(200).optional(),
  offset: z.int().min(0).optional(),
});

export const ListScheduledPaymentsResponseSchema = z.object({
  scheduledPayments: z.array(ScheduledPaymentSchema),
  pagination: z.object({
    total: z.int().nonnegative(),
    limit: z.int().nonnegative(),
    offset: z.int().nonnegative(),
  }),
});

export const ReschedulePaymentInputSchema = z.object({
  apInvoiceId: UUIDSchema,
  scheduledPaymentDate: z.iso.datetime(),
});

export const ReschedulePaymentResponseSchema = z.object({
  apInvoiceId: UUIDSchema,
  scheduledPaymentDate: z.iso.datetime(),
});

export const SetPaymentHoldInputSchema = z.object({
  apInvoiceId: UUIDSchema,
  hold: z.boolean(),
  reason: z.string().optional(),
  clearScheduledDate: z.boolean().optional(),
});

export const SetPaymentHoldResponseSchema = z.object({
  apInvoiceId: UUIDSchema,
  paymentHold: z.boolean(),
  paymentHoldReason: z.string().nullable(),
  scheduledPaymentDate: z.string().nullable(),
});

export const scheduledPaymentsContracts = {
  list: oc
    .input(ListScheduledPaymentsInputSchema)
    .output(ListScheduledPaymentsResponseSchema)
    .route({
      method: "GET",
      summary: "List scheduled invoice payments",
      description:
        "Lists AP invoices with their scheduled payment date, due date, terms, amount, and hold status. Filter by supplier, payment status, hold, and scheduled date range.",
    }),
  reschedule: oc
    .input(ReschedulePaymentInputSchema)
    .output(ReschedulePaymentResponseSchema)
    .route({
      method: "PUT",
      summary: "Reschedule an invoice payment date",
      description:
        "Sets a new scheduled payment date on an AP invoice. Rejected when the invoice is on payment hold.",
    }),
  setHold: oc
    .input(SetPaymentHoldInputSchema)
    .output(SetPaymentHoldResponseSchema)
    .route({
      method: "PUT",
      summary: "Put an invoice payment on hold",
      description:
        "Places or releases a payment hold on an AP invoice with an optional reason, and can clear its scheduled payment date when holding.",
    }),
} as const;

export type ScheduledPayment = z.infer<typeof ScheduledPaymentSchema>;
export type ListScheduledPaymentsInput = z.infer<
  typeof ListScheduledPaymentsInputSchema
>;
export type ListScheduledPaymentsResponse = z.infer<
  typeof ListScheduledPaymentsResponseSchema
>;
export type ReschedulePaymentInput = z.infer<
  typeof ReschedulePaymentInputSchema
>;
export type ReschedulePaymentResponse = z.infer<
  typeof ReschedulePaymentResponseSchema
>;
export type SetPaymentHoldInput = z.infer<typeof SetPaymentHoldInputSchema>;
export type SetPaymentHoldResponse = z.infer<
  typeof SetPaymentHoldResponseSchema
>;
